import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import { Field, Parsing } from "trader.ts/ui.forms";
import { mapPartial } from "trader.ts/utils";
import { isEqual } from "../lib/isequal";
import * as style from "./../../styles/style.css.classes";
import { renderBehindToggle } from "./renderbehindtoggle";
import { renderIf } from "trader.ts/ui.common";

export function checkboxesField<T>(opts: {
  initial: T[];
  values: T[];
  show: (t: T) => string;
  addNoFilterOption: boolean;
  addAllOption?: boolean;
  saveAndLoadInitialValToLocalStorage?: string;
  label?: string;
  renderBehindToggle?: boolean;
  checkboxClass?: string;
  render?: // wrapping in closure for initialization
  () => (
    values: { label: string; checked: boolean; onclick: () => void }[],
  ) => HTMLElement | HTMLElement[];
}): Field<T[]> {
  const lsKey = opts?.saveAndLoadInitialValToLocalStorage
    ? "trader_forms_checkbox_" + opts.saveAndLoadInitialValToLocalStorage
    : null;

  const fromLs: unknown = lsKey
    ? JSON.parse(localStorage.getItem(lsKey) || "{}")
    : null;
  const initialFound = Array.isArray(fromLs)
    ? opts.values.filter((opt) =>
        fromLs.find((str) => isEqual(opts.show(opt), str)),
      )
    : opts.values.filter((opt) =>
        opts.initial.find((opt_b) => isEqual(opt, opt_b)),
      );

  const initialParsed = {
    tag: "parsed" as const,
    parsed: initialFound,
  };

  const parsingS: Source<Parsing<T[]>> = new Source(initialParsed);

  parsingS.observe((p) =>
    console.log("checkboxes: " + JSON.stringify(p, null, 2)),
  );

  function handleClick(opt: T) {
    const curr = parsingS.get();
    const currVs = curr.tag === "parsed" ? curr.parsed : [];
    const selectedCurrently = currVs.some((v) => isEqual(v, opt));
    const selectedNew = selectedCurrently
      ? currVs.filter((v) => !isEqual(v, opt))
      : [...currVs, opt];
    parsingS.set({
      tag: "parsed",
      parsed:
        // We start from the values again to respect sorting
        opts.values.filter((v) => selectedNew.some((nw) => isEqual(v, nw))),
    });
    if (lsKey) {
      localStorage.setItem(lsKey, JSON.stringify(selectedNew.map(opts.show)));
    }
  }

  function handleClickNoFilter() {
    // Clicking on "Geen filter" always removes all selections
    parsingS.set({ tag: "parsed", parsed: [] });
    if (lsKey) {
      localStorage.removeItem(lsKey);
    }
  }
  function handleClickAll() {
    const curr = parsingS.get();
    if (curr.tag === "parsed") {
      if (curr.parsed.length === opts.values.length) {
        parsingS.set({ tag: "parsed", parsed: [] });
        if (lsKey) {
          localStorage.removeItem(lsKey);
        }
      } else {
        parsingS.set({ tag: "parsed", parsed: opts.values });
        if (lsKey) {
          localStorage.setItem(
            lsKey,
            JSON.stringify(opts.values.map(opts.show)),
          );
        }
      }
    }
  }

  function render() {
    const toggleS = new Source(false);
    const renderFunc = opts.render ? opts.render() : defaultRender;
    return dyn(parsingS, (parsing) => {
      if (parsing.tag !== "parsed") {
        return <span>Checkboxesfield error</span>; // Should never happen
      } else {
        const parsed = parsing.parsed;
        const allButton =
          opts.addAllOption === true
            ? {
                label: "Allemaal",
                checked: parsed.length === opts.values.length,
                onclick: handleClickAll,
              }
            : null;
        const noneButton =
          opts.addNoFilterOption === true
            ? {
                label: "Geen filter",
                checked: parsed.length === 0 ? true : false,
                onclick: handleClickNoFilter,
              }
            : null;
        const normalValues = opts.values.map((v) => ({
          label: opts.show(v),
          checked: parsed.some((p) => isEqual(p, v)),
          onclick: () => handleClick(v),
        }));
        const valuesToRender: {
          label: string;
          checked: boolean;
          onclick: () => void;
        }[] = [allButton, ...normalValues, noneButton].compact();

        const rendered = renderFunc(valuesToRender, {
          checkboxClass: opts.checkboxClass || "",
        });

        if (opts.renderBehindToggle && opts.label) {
          return renderBehindToggle(toggleS, opts.label, rendered);
        }
        if (opts.label) {
          return (
            <label>
              <div className={"label-text"}>{opts.label}</div>
              {rendered}
            </label>
          );
        }
        return rendered;
      }
    });
  }

  return { s: parsingS, render, cleanup: () => {} };
}

function defaultRender(
  values: { label: string; checked: boolean; onclick: () => void }[],
  opts: { checkboxClass: string },
) {
  return (
    <div>
      {values.map((v) => (
        <label className={style.checkbox + opts.checkboxClass}>
          <input
            type="checkbox"
            checked={v.checked ? "true" : undefined}
            onclick={v.onclick}
          />
          {v.label}
        </label>
      ))}
    </div>
  );
}

export function wrapSelectboxesInPanel(
  title: string,
  html: HTMLElement | HTMLElement[],
) {
  return (
    <div style="position: relative; top: -5px;">
      <div>
        <label>{title}</label>
      </div>
      <div
        className={style.panel}
        style="background-color: var(--grey-100); border: none;"
      >
        {html}
      </div>
    </div>
  );
}

export function checkboxesFieldRenderMultiselectWithPopup() {
  const showPopupS = new Source(false);
  return function (
    values: { label: string; checked: boolean; onclick: () => void }[],
  ) {
    const selectbox = (
      <div
        className={style.input_like + style.select_like}
        onclick={(ev) => {
          ev.preventDefault();
          showPopupS.set(!showPopupS.get());
        }}
      >
        {mapPartial(values, (v) => (v.checked ? v.label : null)).join(", ")}
      </div>
    );
    const popup = renderIf(showPopupS, () => (
      <div>
        <div
          className={style.popup_backdrop}
          style="background-color: transparent"
          onclick={() => showPopupS.set(false)}
        ></div>
        <div className={style.popup_dropdown}>
          {values.map((v) => (
            <label className={style.checkbox}>
              <input
                type="checkbox"
                checked={v.checked ? "true" : undefined}
                onclick={(ev) => {
                  ev.stopPropagation();
                  v.onclick();
                }}
              />
              {v.label}
            </label>
          ))}
        </div>
      </div>
    ));
    return (
      <div>
        {selectbox}
        <div style="position: relative; margin-top: -16px;">{popup}</div>
      </div>
    );
  };
}
